/* Module registry for the console.
 *
 * The dashboard is a shell that hosts modules; the Turn Tracker is the first
 * one. A module registers itself once its components exist:
 *
 *   import { apps } from '../apps.js';
 *
 *   apps.register({
 *     id: 'turn-tracker',
 *     name: 'Turn Tracker',
 *     blurb: 'The turn grid…',
 *     icon: '▦',
 *     page: TurnTracker,      // fills the body
 *     header: TurnTrackerNav, // optional: its own top-bar nav
 *     badge: someComputed,    // optional: a quiet count on the switcher
 *     landing: false,         // optional: never the module a console falls back to
 *     available: someComputed // optional: false takes a licensed module off the list
 *   });
 *
 * A module's file is imported for that side effect alone, from js/main.js —
 * nothing reads what it exports, so the import is the registration.
 *
 * The shell owns the brand, the module switcher and the sync pill; everything
 * else on screen belongs to whichever module is active. Nothing here knows what
 * a turn is, which is the point.
 *
 * ── LICENSING ───────────────────────────────────────────────────────────────
 *
 * A salon is only offered the modules it is licensed for. An unlicensed one is
 * not listed, not selectable, and cannot be left active — it is not part of
 * this console as far as that salon is concerned.
 *
 * This reverses an earlier decision, and the argument it lost to is worth
 * keeping written down: an entry marked "not on your plan" is something a salon
 * can ring up and ask about, and a menu that silently loses an entry is a
 * product nobody knows to want. That is true, and it is a case for advertising
 * — which is a thing to do deliberately, on a page built for it, rather than by
 * leaving dead controls in the one menu the front desk uses all day. A switcher
 * whose entries mostly refuse to switch teaches people not to open it.
 *
 * Two questions, and they are not the same one:
 *
 *   offered()  is this module on this salon's plan? Decides whether it appears
 *              at all — in the switcher, and as somewhere `active` may land.
 *   stateOf()  what is it, once offered? 'planned' for something not built yet,
 *              'ready' otherwise. 'locked' survives for a module that is active
 *              at the moment its licence is withdrawn, and for the fail-open
 *              case below; the stub still explains itself when it happens.
 *
 * `feature` is the licence id, and it defaults to the module id, so a module
 * whose name in js/rules/licence.js matches its registration needs to say
 * nothing at all. See js/licensing.js for what happens when nothing is known:
 * everything is licensed, always. That is what makes this file's behaviour on a
 * project with no licensing schema identical to what it was before — and it is
 * why hiding is safe here. The failure mode of an unknown answer is a switcher
 * with too much in it, never one with too little.
 */
import * as Vue from 'vue';
import { licensing } from './licensing.js';
import * as modules from './rules/modules.js';

var KEY = 'dreamspa.dash.app';

var registry = Vue.reactive([]);
// Only ever set by an explicit choice (or a remembered one). Registration must
// not resolve a default: modules register in script order, so whichever loaded
// first would win — and the placeholders below load before any real module.
var activeId = Vue.ref(null);

// Entries with no module behind them yet. They are listed so the switcher is
// a real switcher rather than a menu of one, and they render a stub saying so.
// Delete or rename freely — nothing depends on these ids.
var PLANNED = [
  {
    id: 'reports', name: 'Reports', icon: '▤', status: 'planned',
    blurb: 'Revenue, appointment volume and staff performance by day, week and month.'
  },
  {
    id: 'reviews', name: 'Reviews', icon: '★', status: 'planned',
    blurb: 'Google, Yelp and ClassPass reviews in one queue, with replies.'
  }
];

function stored() {
  try { return window.localStorage.getItem(KEY); } catch (e) { return null; }
}

function remember(id) {
  try { window.localStorage.setItem(KEY, id); } catch (e) { /* noop */ }
}

/* Every decision below is in js/rules/modules.js, tested. What is left here is
 * the binding: `licensing.licensed` is the answer those rules take as an
 * argument, and reading it inside a computed is what makes the switcher redraw
 * when the licence lands — which is usually a moment after the menu is built,
 * and after every module has registered.
 */
function licensed(feature) { return licensing.licensed(feature); }

function stateOf(app) { return modules.stateOf(app, licensed); }
function locked(app) { return stateOf(app) === 'locked'; }
function offered(app) { return modules.offers(app, licensed); }

// The registry with each entry's current state on it, for the switcher — and
// only what this salon is offered.
var listed = Vue.computed(function () {
  return modules.offeredList(registry, licensed).map(function (a) {
    return Object.assign({}, a, { state: stateOf(a) });
  });
});

function list() { return listed; }

var switchable = Vue.computed(function () {
  return modules.switchable(registry, licensed);
});

function badge(app) { return modules.badgeOf(app); }

function get(id) {
  return registry.find(function (a) { return a.id === id; }) || null;
}

// The modules that can actually be used: built, and licensed. This is what the
// fallback in `active` picks from, so a salon whose licence has lapsed lands on
// whatever they DO have rather than on a locked screen.
function ready() { return modules.readyList(registry, licensed); }

function register(app) {
  if (get(app.id)) return;
  registry.push(Object.assign({ status: 'ready', icon: '▦', feature: app.id }, app));
  sortRegistry();
}

// Built modules first, then the placeholders, each alphabetical.
function sortRegistry() {
  registry.sort(function (a, b) {
    var rank = (a.status === 'planned' ? 1 : 0) - (b.status === 'planned' ? 1 : 0);
    return rank || a.name.localeCompare(b.name);
  });
}

function setActive(id) {
  if (!get(id)) return false;
  activeId.value = id;
  remember(id);
  return true;
}

/* Resolved on read rather than stored, so every module has had its chance to
 * register by the time this is asked for. A remembered id that no longer exists
 * (renamed, removed) falls through to the first built module instead of leaving
 * the console on a blank page.
 *
 * A remembered id the salon is no longer OFFERED falls through the same way,
 * and that part is load-bearing now rather than tidy. The switcher is hidden
 * when there is one module left, so a salon that had two, chose the second, and
 * then lapsed off it would otherwise be parked on a stub explaining what they
 * cannot have, with nothing on screen to leave it by. Dropping them onto the
 * module they still have is the only exit that does not require a menu that is
 * no longer there.
 */
var active = Vue.computed(function () {
  return modules.resolveActive(registry, activeId.value, licensed);
});

activeId.value = stored();

PLANNED.forEach(register);

export const apps = {
  list: list,
  get: get,
  ready: ready,
  offered: offered,
  switchable: switchable,
  register: register,
  setActive: setActive,
  stateOf: stateOf,
  locked: locked,
  badge: badge,
  active: active,
  activeId: activeId
};
